import React, { ReactElement } from 'react';
import { Toast, TOAST_TYPE } from 'b2b-apps/shop/common/components/Toast';
import { useTranslate } from 'b2b-apps/shop/i18n/hooks';
import { MY_ACCOUNT_MESSAGE } from 'b2b-apps/shop/myAccount/common/utils/messages';
import { ComponentType, Props } from './';

const MyAccountToast: ComponentType = ({ messageId }: Props): ReactElement => {
  const t = useTranslate();
  switch (messageId) {
    case MY_ACCOUNT_MESSAGE.duplicationErrorAddToWishlist:
      return (
        <Toast
          header={t('Product is already on this wishlist')}
          type={TOAST_TYPE.warning}
        />
      );

    case MY_ACCOUNT_MESSAGE.errorAddToWishlist:
      return (
        <Toast
          header={t('Could not add the product, try again later')}
          type={TOAST_TYPE.error}
        />
      );

    case MY_ACCOUNT_MESSAGE.errorChangePassword:
      return (
        <Toast
          header={t('Could not change password, try again')}
          type={TOAST_TYPE.error}
        />
      );

    case MY_ACCOUNT_MESSAGE.errorCreateAndAddToWishlist:
      return (
        <Toast
          header={t(
            'Could not add the product to a new wishlist, try again later',
          )}
          type={TOAST_TYPE.error}
        />
      );

    case MY_ACCOUNT_MESSAGE.errorAddAddress:
    case MY_ACCOUNT_MESSAGE.errorCreateAddress:
      return (
        <Toast
          header={t('Could not add the address, try again later')}
          type={TOAST_TYPE.error}
        />
      );

    case MY_ACCOUNT_MESSAGE.errorCreateUser:
      return (
        <Toast
          header={t('Could not add the user, try again later')}
          type={TOAST_TYPE.error}
        />
      );

    case MY_ACCOUNT_MESSAGE.errorCreateWishlist:
      return (
        <Toast
          header={t('Could not add the wishlist, try again later')}
          type={TOAST_TYPE.error}
        />
      );

    case MY_ACCOUNT_MESSAGE.errorEditAddress:
      return (
        <Toast
          header={t('Could not edit the address, try again later')}
          type={TOAST_TYPE.error}
        />
      );

    case MY_ACCOUNT_MESSAGE.errorEditContactInformation:
      return (
        <Toast
          header={t('Could not edit contact information, try again later')}
          type={TOAST_TYPE.error}
        />
      );

    case MY_ACCOUNT_MESSAGE.errorRemoveAddress:
      return (
        <Toast
          header={t('Could not delete the address, try again later')}
          type={TOAST_TYPE.error}
        />
      );

    case MY_ACCOUNT_MESSAGE.errorRemoveUser:
      return (
        <Toast
          header={t('Your changes could not be saved, please try again later')}
          type={TOAST_TYPE.error}
        />
      );

    case MY_ACCOUNT_MESSAGE.errorRemoveWishlist:
      return (
        <Toast
          header={t('Could not delete the wishlist, try again later')}
          type={TOAST_TYPE.error}
        />
      );

    case MY_ACCOUNT_MESSAGE.errorUpdateUser:
      return (
        <Toast
          header={t('Could not edit the user, try again later')}
          type={TOAST_TYPE.error}
        />
      );

    case MY_ACCOUNT_MESSAGE.errorRemoveWishlistItem:
      return (
        <Toast
          header={t('Could not delete the wishlist item, try again later')}
          type={TOAST_TYPE.error}
        />
      );

    case MY_ACCOUNT_MESSAGE.errorRemoveWishlistItems:
      return (
        <Toast
          header={t('Could not delete wishlist items, try again later')}
          type={TOAST_TYPE.error}
        />
      );

    case MY_ACCOUNT_MESSAGE.errorUpdateWishlist:
      return (
        <Toast
          header={t('Could not edit the wishlist, try again later')}
          type={TOAST_TYPE.error}
        />
      );

    case MY_ACCOUNT_MESSAGE.errorUpdateWishlistItem:
      return (
        <Toast
          header={t('Could not edit the wishlist item, try again later')}
          type={TOAST_TYPE.error}
        />
      );

    case MY_ACCOUNT_MESSAGE.errorUpdateWishlistReference:
      return (
        <Toast
          header={t('Could not update wishlist reference, try again later')}
          type={TOAST_TYPE.error}
        />
      );

    case MY_ACCOUNT_MESSAGE.successAddToWishlist:
      return (
        <Toast
          header={t('Item has been added to the wishlist')}
          type={TOAST_TYPE.success}
        />
      );

    case MY_ACCOUNT_MESSAGE.successChangePassword:
      return (
        <Toast
          header={t('Your password has been successfully changed')}
          type={TOAST_TYPE.success}
        />
      );

    case MY_ACCOUNT_MESSAGE.successCreateAndAddToWishlist:
      return (
        <Toast
          header={t('Item has been added to newly created wishlist')}
          type={TOAST_TYPE.success}
        />
      );

    case MY_ACCOUNT_MESSAGE.successAddAddress:
    case MY_ACCOUNT_MESSAGE.successCreateAddress:
      return (
        <Toast
          header={t('New address has been added')}
          type={TOAST_TYPE.success}
        />
      );

    case MY_ACCOUNT_MESSAGE.successCreateUser:
      return (
        <Toast
          header={t('New user has been added')}
          type={TOAST_TYPE.success}
        />
      );

    case MY_ACCOUNT_MESSAGE.successCreateWishlist:
      return (
        <Toast
          header={t('New wishlist has been added')}
          type={TOAST_TYPE.success}
        />
      );

    case MY_ACCOUNT_MESSAGE.successEditAddress:
      return (
        <Toast
          header={t('Address edited successfully')}
          type={TOAST_TYPE.success}
        />
      );

    case MY_ACCOUNT_MESSAGE.successEditContactInformation:
      return (
        <Toast
          header={t('Contact information edited successfully')}
          type={TOAST_TYPE.success}
        />
      );

    case MY_ACCOUNT_MESSAGE.successRemoveAddress:
      return (
        <Toast
          header={t('Address removed successfully')}
          type={TOAST_TYPE.success}
        />
      );

    case MY_ACCOUNT_MESSAGE.successRemoveUser:
      return (
        <Toast
          header={t('User has been successfully removed')}
          type={TOAST_TYPE.success}
        />
      );

    case MY_ACCOUNT_MESSAGE.successRemoveWishlist:
      return (
        <Toast
          header={t('Wishlist removed successfully')}
          type={TOAST_TYPE.success}
        />
      );

    case MY_ACCOUNT_MESSAGE.successRemoveItem:
      return (
        <Toast
          header={t('Item removed successfully')}
          type={TOAST_TYPE.success}
        />
      );

    case MY_ACCOUNT_MESSAGE.successRemoveItems:
      return (
        <Toast
          header={t('Items removed successfully')}
          type={TOAST_TYPE.success}
        />
      );

    case MY_ACCOUNT_MESSAGE.successUpdateUser:
      return (
        <Toast
          header={t('User edited successfully')}
          type={TOAST_TYPE.success}
        />
      );

    case MY_ACCOUNT_MESSAGE.successUpdateWishlist:
      return (
        <Toast
          header={t('Wishlist edited successfully')}
          type={TOAST_TYPE.success}
        />
      );

    default:
      return (
        <Toast
          header={t('Could not connect to the my account.')}
          type={TOAST_TYPE.error}
        />
      );
  }
};

export { MyAccountToast };
